/**
 * pricingService.ts — Standalone token USD price fetching with Moralis -> Jupiter v3 -> null fallback.
 *
 * Design constraints:
 * - ZERO imports from Supabase, wallet services, or AppKit
 * - Independently testable (no wallet connection state required)
 * - Module-level cache with 5-minute TTL (survives page session, avoids repeated API calls)
 *
 * Fallback chain:
 *   Tier 1: Moralis Solana Gateway (live price, sub-second)
 *   Tier 2: Jupiter Price API v3 (on-chain DEX aggregator, canonical Solana price source)
 *   Tier 3: null — caller uses token.manualUsdRate from DB
 *   Last resort: stale cached price (if previous successful fetch exists)
 */

const MORALIS_SOLANA_BASE = "https://solana-gateway.moralis.io";
const MORALIS_API_KEY =
  (typeof import.meta !== "undefined" &&
    (import.meta as { env?: Record<string, string> }).env?.VITE_MORALIS_API_KEY) ||
  "";

// 5-minute TTL cache — module-level so it survives across calls within a session
let _cachedPrice: number | null = null;
let _cacheTs = 0;
const CACHE_TTL_MS = 5 * 60 * 1000;

/**
 * Fetch the USD price of a token by mint address.
 *
 * Uses a Moralis -> Jupiter v3 -> null fallback chain.
 * On total failure, returns the stale cache value if one exists from a prior successful fetch.
 *
 * @param mintAddress - Solana mint address (e.g. "So11111111111111111111111111111111111111112" for SOL)
 * @returns USD price as a number, or null if unavailable (caller should use token.manualUsdRate)
 */
export async function fetchTokenUsdPrice(mintAddress: string): Promise<number | null> {
  const now = Date.now();

  // Return fresh cached price if within TTL
  if (_cachedPrice != null && now - _cacheTs < CACHE_TTL_MS) {
    return _cachedPrice;
  }

  // Tier 1: Moralis Solana Gateway
  try {
    const resp = await fetch(
      `${MORALIS_SOLANA_BASE}/token/mainnet/${mintAddress}/price`,
      { headers: { accept: "application/json", "X-API-Key": MORALIS_API_KEY } }
    );
    if (resp.ok) {
      const data = await resp.json() as { usdPrice?: number };
      if (data.usdPrice != null && data.usdPrice > 0) {
        _cachedPrice = data.usdPrice;
        _cacheTs = now;
        if (import.meta.env.DEV) console.log(`[pricing] source: moralis, price: $${data.usdPrice}`);
        return _cachedPrice;
      }
      console.warn("[pricing] moralis failed: response ok but usdPrice missing or zero");
    } else {
      console.warn(`[pricing] moralis failed: HTTP ${resp.status}`);
    }
  } catch (err) {
    console.warn("[pricing] moralis failed:", err instanceof Error ? err.message : String(err));
  }

  // Tier 2: Jupiter Price API v3 (on-chain DEX aggregator)
  // No API key sent client-side to avoid key exposure risk; 401 is treated as a fallback trigger
  try {
    const resp = await fetch(
      `https://api.jup.ag/price/v3?ids=${mintAddress}`,
      { headers: { accept: "application/json" } }
    );
    if (resp.ok) {
      const data = await resp.json() as Record<string, { usdPrice?: number }>;
      const usdPrice = data[mintAddress]?.usdPrice;
      if (usdPrice != null && usdPrice > 0) {
        _cachedPrice = usdPrice;
        _cacheTs = now;
        if (import.meta.env.DEV) console.log(`[pricing] source: jupiter, price: $${usdPrice}`);
        return _cachedPrice;
      }
      console.warn("[pricing] jupiter failed: response ok but usdPrice missing or zero");
    } else {
      console.warn(`[pricing] jupiter failed: HTTP ${resp.status}`);
    }
  } catch (err) {
    console.warn("[pricing] jupiter failed:", err instanceof Error ? err.message : String(err));
  }

  // Tier 3: Return stale cache as last resort (better than null if we had a prior price)
  if (_cachedPrice != null) {
    console.warn("[pricing] both APIs failed, using stale cache:", _cachedPrice);
    return _cachedPrice;
  }

  // Total failure — caller uses token.manualUsdRate from DB
  return null;
}

/**
 * Reset the module-level price cache.
 * Only for use in tests — do NOT call in production code.
 */
export function resetPriceCacheForTesting(): void {
  _cachedPrice = null;
  _cacheTs = 0;
}
